import { useState, useEffect, useRef } from "react";

import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CCardFooter,
  CLink,
  CPagination,
  CSelect
} from "@coreui/react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { bindActionCreators } from "redux";

import { logout } from "../../../../actions/baseActions";
import { setObj } from "../../../../actions/noteActions";
import Breadcrumb from "../../../../components/Breadcrumb";
import Loading from "../../../../components/Loading";
import ProtectedRoute from "../../../../components/ProtectedRoute";
import URL from "../../../../config/api";
import axios from "../../../../config/axios";
import Statistics from "../Show/statistics";
import { hasGameficandusModule } from "../../../../utils/accessSession";
import "./styles.css";

const ClassroomMe = props => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(false);
  const [statisticsName, setStatisticsName] = useState("");
  const [classroomsListPage, setClassroomsListPage] = useState(1);
  const [classroomsListLastPage, setClassroomsListLastPage] = useState(1);
  const [yearSelected, setYearSelected] = useState(new Date().getFullYear());

  const [activeRole, setActiveRole] = useState(null);
  const [rolesList, setRolesList] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const initialRender = useRef(true);

  // Função para buscar o nome da escola de um perfil
  const fetchSchoolNameForRole = async (role, year) => {
    if (
      !role.school_id &&
      role.role_id !== 6 &&
      role.role_id !== 4 &&
      role.role_id !== 5
    ) {
      return null; // não tem escola
    }

    const params = {
      start: `${year}-01-01`,
      end: `${year}-12-31`,
      page: 1,
      page_size: 1 // só precisamos de um item para pegar o nome
    };

    switch (role.role_id) {
      case 2:
        if (role.group_id) params.group_id = role.group_id;
        break;
      case 3:
        if (role.region_id) params.region_id = role.region_id;
        break;
      case 4:
      case 5:
      case 6:
        if (role.school_id) params.school_id = role.school_id;
        break;
      default:
        return null;
    }

    try {
      const response = await axios.get(
        `${URL.api}community/room_user/listRoomsByUser`,
        {
          params,
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );
      const data = response.data?.data || [];
      if (data.length > 0 && data[0].school_name) {
        return data[0].school_name;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Carrega os nomes de todos os perfis em paralelo
  const loadAllSchoolNames = async (roles, year) => {
    const promises = roles.map(role => fetchSchoolNameForRole(role, year));
    const results = await Promise.all(promises);

    // Atualiza os labels com os nomes obtidos (ou mantém o ID se não encontrado)
    setRolesList(prev =>
      prev.map((role, index) => {
        const schoolName =
          results[index] ||
          `ID ${role.school_id || role.region_id || role.group_id}`;
        let label = "";
        switch (role.role_id) {
          case 2:
            label = `ADM Grupo - ${schoolName}`;
            break;
          case 3:
            label = `ADM Regional - ${schoolName}`;
            break;
          case 4:
            label = `ADM Escolar - ${schoolName}`;
            break;
          case 5:
            label = `Coord. Pedagógico - ${schoolName}`;
            break;
          case 6:
            label = `Professor - ${schoolName}`;
            break;
          default:
            label = `Perfil ${role.role_id}`;
            break;
        }
        return { ...role, label };
      })
    );
  };

  // Carrega a lista de perfis do usuário ao montar
  useEffect(() => {
    const userRoles = props.user?.user_roles || [];
    if (userRoles.length === 0) {
      setLoadingRoles(false);
      return;
    }

    // Constrói lista inicial com labels provisórias (IDs)
    const formatted = userRoles.map((role, index) => {
      let label = "";
      switch (role.role_id) {
        case 2:
          label = `ADM Grupo - ID ${role.group_id}`;
          break;
        case 3:
          label = `ADM Regional - ID ${role.region_id}`;
          break;
        case 4:
          label = `ADM Escolar - ID ${role.school_id}`;
          break;
        case 5:
          label = `Coord. Pedagógico - ID ${role.school_id}`;
          break;
        case 6:
          label = `Professor - ID ${role.school_id}`;
          break;
        default:
          label = `Perfil ${role.role_id}`;
          break;
      }
      return { ...role, label, index };
    });

    setRolesList(formatted);
    setLoadingRoles(false);

    const primary = formatted.find(r => r.principal === true);
    setActiveRole(primary || formatted[0]);

    // Carrega os nomes de todos os perfis em segundo plano (não bloqueia o loading)
    loadAllSchoolNames(formatted, yearSelected);
  }, [props.user]);

  // Recarrega as turmas quando o perfil ativo, o ano ou a página mudar
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (activeRole) {
      pullClassrooms(classroomsListPage);
    }
  }, [yearSelected, classroomsListPage, activeRole]);

  const newInfo = response => {
    if (response === "Unauthorized") {
      props.logout(props.user.token);
    } else {
      toastr.info("Aviso", response);
    }
  };

  const pullClassrooms = async page => {
    if (!activeRole) return;

    setLoading(true);
    const currYear = yearSelected || new Date().getFullYear();

    const params = {
      start: `${currYear}-01-01`,
      end: `${currYear}-12-31`,
      page: page || 1,
      page_size: 28
    };

    switch (activeRole.role_id) {
      case 2:
        if (activeRole.group_id) params.group_id = activeRole.group_id;
        break;
      case 3:
        if (activeRole.region_id) params.region_id = activeRole.region_id;
        break;
      case 4:
      case 5:
      case 6:
        if (activeRole.school_id) params.school_id = activeRole.school_id;
        break;
      default:
        break;
    }

    try {
      const response = await axios.get(
        `${URL.api}community/room_user/listRoomsByUser`,
        {
          params,
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const data = response.data.data || [];
      setRooms(data);
      setLoading(false);
      setClassroomsListPage(response.data.meta.current_page);
      setClassroomsListLastPage(response.data.meta.last_page);
    } catch (error) {
      newInfo("Você não está participando de nenhuma turma nesse ano!");
      setRooms([]);
      setLoading(false);
      setClassroomsListPage(1);
      setClassroomsListLastPage(1);
    }
  };

  // Carrega as turmas na montagem inicial
  useEffect(() => {
    if (activeRole) {
      pullClassrooms(1);
    }
    props.setObj(16, props.currentRoute, props.currentRoute);
  }, [activeRole]);

  return (
    <>
      <Breadcrumb current={props.currentRoute} />
      {loading || loadingRoles ? (
        <Loading top="45" left="50" />
      ) : (
        <>
          {!!statistics && (
            <Statistics
              show
              onClose={() => setStatistics(false)}
              room_id={statistics}
              name={statisticsName}
            />
          )}
          <CCard>
            <CCardHeader className="d-flex align-items-center justify-content-around">
              <CRow className="w-100 d-flex align-items-center">
                <CCol md={rolesList.length > 1 ? "6" : "9"}>
                  <div>
                    <h4 className="card-title mb-1">Turmas</h4>
                    <div className="small text-muted">
                      Veja as turmas do perfil selecionado
                    </div>
                  </div>
                </CCol>
                {rolesList.length > 1 && (
                  <CCol md="3">
                    <CSelect
                      value={activeRole?.index ?? 0}
                      onChange={e => {
                        const idx = Number.parseInt(e.target.value);
                        const selected = rolesList.find(r => r.index === idx);
                        if (selected) {
                          setActiveRole(selected);
                          setClassroomsListPage(1);
                        }
                      }}
                    >
                      {rolesList.map(role => (
                        <option key={role.index} value={role.index}>
                          {role.label}
                        </option>
                      ))}
                    </CSelect>
                  </CCol>
                )}
                <CCol md={rolesList.length > 1 ? "3" : "3"}>
                  <CSelect
                    value={yearSelected}
                    onChange={e => {
                      setYearSelected(Number.parseInt(e.target.value));
                    }}
                  >
                    <option value="2022">2022</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                  </CSelect>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody className="pb-1">
              <CRow>
                {rooms.length > 0 ? (
                  rooms.map((room, idx) => (
                    <CCol sm="4" lg="3" className="mb-3" key={idx}>
                      <CLink
                        to={`/classroom/${room.id}`}
                        className="text-decoration-none"
                      >
                        <CCard className="card-room">
                          <CCardHeader
                            className="bg-primary d-flex justify-content-end flex-column"
                            style={{ height: "120px" }}
                          >
                            <h4
                              className="card-title text-white mb-2 text-ellipsis"
                              title={room.name}
                            >
                              {room.name}
                            </h4>
                            <div
                              className="small text-ellipsis"
                              style={{ opacity: 0.6 }}
                            >
                              {room.external_id && (
                                <>
                                  <i className="fa fa-graduation-cap mr-2"></i>
                                  {room.external_id}
                                  <span
                                    className="mx-2"
                                    style={{ opacity: 0.4 }}
                                  >
                                    ·
                                  </span>
                                </>
                              )}
                              <i className="fa fa-university mr-2"></i>
                              {room.school_name}
                            </div>
                          </CCardHeader>
                          <CCardFooter className="d-flex text-muted">
                            <span className="mr-3" style={{ flex: 1 }}>
                              <i className="fa fa-user mr-2"></i>{" "}
                              {room.num_users}
                            </span>
                            {!["A", "S", "V"].includes(
                              props.general.active_roles.type
                            ) && (
                              <CLink
                                className="mr-3"
                                to="#"
                                onClick={() => {
                                  setStatistics(room.id);
                                  setStatisticsName(room.name);
                                }}
                              >
                                <i className="fa fa-pie-chart text-muted card-icon"></i>
                              </CLink>
                            )}
                            {!["A", "S", "V"].includes(
                              props.general.active_roles.type
                            ) &&
                              hasGameficandusModule(
                                props.user.user_modules
                              ) && (
                                <CLink
                                  className="mr-3"
                                  to={`/classroom/${room.id}/certificates`}
                                >
                                  <i className="fa fa-certificate text-muted card-icon"></i>
                                </CLink>
                              )}
                            <CLink
                              className="mr-3"
                              to={`/classroom/${room.id}/activities`}
                            >
                              <i className="fa fa-check-square text-muted card-icon"></i>
                            </CLink>
                            <CLink to={`/classroom/${room.id}/docs`}>
                              <i className="fa fa-folder text-muted card-icon"></i>
                            </CLink>
                          </CCardFooter>
                        </CCard>
                      </CLink>
                    </CCol>
                  ))
                ) : (
                  <div className="p-3 text-center w-100 text-muted mb-3">
                    Você não está participando de nenhuma turma neste perfil/ano
                  </div>
                )}
              </CRow>
            </CCardBody>
            <CCardFooter className="pb-0">
              <CPagination
                align="end"
                className="mt-1"
                activePage={classroomsListPage}
                pages={classroomsListLastPage}
                onActivePageChange={page => setClassroomsListPage(page)}
              />
            </CCardFooter>
          </CCard>
        </>
      )}
      <ProtectedRoute routeId={props.currentRoute} />
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      logout,
      setObj
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ClassroomMe);
